import { useFormatBreederContactInfo } from '@application/api';
import { useImageLightboxState } from '@application/state';
import {
  formatApiPrice,
  formatDateByCountry,
  formatLocation,
  formatPedigreeType,
  useDetectLanguage,
  useRouter,
  useSlideshow,
} from '@application/utils';
import type { LitterGetLitterQuery } from '@gql/graphql';
import { ParentTypeEnum } from '@gql/graphql';
import { PLACEHOLDER_IMAGE_DOG } from '@presentation/constants';
import type { DogParentData, LitterPageBreederData, LitterPageProps } from '@presentation/pages';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import { useFormatBreeds } from '../../BreedListPageController/api/useFormatBreeds';

const formatParentName = (name: string | null | undefined) => {
  if (!name) return name;
  const cleanName = name.trimStart();
  if (!cleanName.includes(' ')) return name;

  const splits = cleanName.split(' ');

  if (splits[1].trim().length === 0) {
    return splits[0];
  }

  return splits[0].concat(' ').concat(splits[1].charAt(0).toUpperCase()).concat('.');
};

export const useFormatLitter = (
  litter?: NonNullable<LitterGetLitterQuery['findManyLitter']>[number] | null,
) => {
  const { translate, countryCode } = useRouter();

  const slideshow = useSlideshow(
    (litter?.imgs ?? []).map((image) => ({
      src: image.medium ?? image.large ?? '',
      thumbnail: image.small ?? '',
    })),
  );
  const { contactInfo } = useFormatBreederContactInfo(litter?.breeder);
  const { formattedBreeds } = useFormatBreeds(litter?.dogBreed ? [litter?.dogBreed] : undefined);

  const { open } = useImageLightboxState();

  const formatQualityField = useCallback(
    (value: boolean | undefined | null, translationOptionsKey: string) => {
      if (value == null) return;
      return translate(`${translationOptionsKey}.${value ? 'true' : 'false'}`);
    },
    [translate],
  );

  const formattedLitter: Partial<LitterPageProps> = useMemo(() => {
    if (!litter) return {};

    const isBorn = (litter.malesCount ?? 0) + (litter.femalesCount ?? 0) > 0;
    return {
      ...(slideshow.items.length ? { slideshow } : {}),
      contactInfo,
      breedName: litter.dogBreed?.translations?.[0]?.name,
      hasPedigree: litter.pedigree !== null,
      parents: (litter.parents ?? []).map(
        (parent) =>
          ({
            name: formatParentName(parent.parent?.name),
            image: parent.parent?.imgs?.[0]?.small ?? PLACEHOLDER_IMAGE_DOG,
            type: parent.parentType,
            pedigreeType: formatPedigreeType(translate, parent.parent?.dogInfos?.pedigree),
            pedigreeNumber: parent.parent?.dogInfos?.pedigreeNumber ?? undefined,
            titles: parent.parent?.dogInfos?.competitionTitles ?? undefined,
            age: parent.parent?.birthday ? dayjs(parent.parent.birthday).toNow(true) : undefined,
            microchipNumber: parent.parent?.dogInfos?.microshipNumber ?? undefined,
            testResults: parent.parent?.dogInfos?.healthTestsResults ?? undefined,
            onImageClick: () =>
              parent.parent && (parent.parent?.imgs ?? []).length > 0
                ? open(parent.parent.imgs.map((x) => x.large as string))
                : null,
          } as DogParentData),
      ),
      description: litter.description,
      breedCard: formattedBreeds?.[0],
      details: [
        {
          label: translate('glossary.birthday'),
          value: litter.birthday && formatDateByCountry(litter.birthday, countryCode),
        },
        {
          label: translate('glossary.availability'),
          value:
            litter.availabilityDate && formatDateByCountry(litter.availabilityDate, countryCode),
        },
        {
          label: translate('glossary.location'),
          value: formatLocation(
            {
              country: litter.breeder.account?.address?.country,
              region: litter.breeder.account?.address?.region,
            },
            translate,
          ),
        },
        {
          label: translate('glossary.price'),
          value: formatApiPrice({ ...litter }),
        },
        ...(isBorn
          ? [
              {
                label: translate('glossary.totalPuppies'),
                value: translate('glossary.totalPuppiesValue')
                  .replaceAll('$males', (litter.malesCount ?? 0).toString())
                  .replaceAll('$females', (litter.femalesCount ?? 0).toString()),
              },
              {
                label: translate('glossary.puppiesLeft'),
                value: translate('glossary.puppiesLeftValue')
                  .replaceAll('$males', (litter.malesLeftCount ?? 0).toString())
                  .replaceAll('$females', (litter.femalesLeftCount ?? 0).toString()),
              },
              {
                label: translate('glossary.wormed'),
                value: formatQualityField(litter.dewormed, 'common.options.wormed'),
              },
              ...(litter.vaccinated == null
                ? []
                : [
                    {
                      label: translate('glossary.vaccinated'),
                      value: formatQualityField(litter.vaccinated, 'common.options.vaccinated'),
                    },
                  ]),
              ...(litter.vaccinationDate == null
                ? []
                : [
                    {
                      label: translate('glossary.vaccinationDate'),
                      value:
                        litter.vaccinationDate &&
                        formatDateByCountry(litter.vaccinationDate, countryCode),
                    },
                  ]),
              {
                label: translate('glossary.chipped'),
                value: formatQualityField(litter.microchipped, 'common.options.chipped'),
              },
              {
                label: translate('glossary.motherMicrochipNumber'),
                value:
                  litter.parents.find((p) => p.parentType === ParentTypeEnum.MOTHER)?.parent
                    ?.dogInfos?.microshipNumber ?? undefined,
              },
            ]
          : []),
        ...(isBorn
          ? [
              {
                label: translate('glossary.pupsMicrochipNumber'),
                value: translate('glossary.toSpecify'),
              },
            ]
          : []),
        {
          label: translate('glossary.pedigree'),
          value: formatPedigreeType(translate, litter.pedigree),
        },
      ],
    } as Partial<LitterPageProps>;
  }, [
    contactInfo,
    countryCode,
    formatQualityField,
    formattedBreeds,
    litter,
    open,
    slideshow,
    translate,
  ]);

  const imagesToPreload = useMemo(() => {
    const firstSlideshowItem = formattedLitter?.slideshow?.items[0];
    if (firstSlideshowItem && !firstSlideshowItem.isVideo) {
      return [firstSlideshowItem.src];
    }
    return [];
  }, [formattedLitter?.slideshow?.items]);

  const formattedBreeder: LitterPageBreederData | undefined = useMemo(() => {
    if (!litter) return;
    return { id: litter.breeder.id, whatsappNumber: litter.breeder.whatsappNumber };
  }, [litter]);

  const { mostProbableLanguage: litterLanguage, detectedLanguages } = useDetectLanguage(
    litter?.description,
  );

  return { formattedLitter, imagesToPreload, formattedBreeder, litterLanguage };
};
